import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { PaymentMethod, useTranslation } from "@next-platform/client-common-core";
import { useMemo } from "react";

import { Col } from "@/components";
import PaymentIcon from "@/components/Checkout/PaymentIcon";
import Trans from "@/components/Trans/Trans";
import { PaymentMethodIconConfig } from "@/types/subscription";
import { cn } from "@/utils";
import { CARD_BRAND_CONFIG, getPaymentMethodConfig, ResolvedPaymentConfig } from "@/utils/subscription";

interface IPaymentMethodsDisplayProps {
  paymentMethods: string[];
  className?: string;
}

const PaymentItem = ({ iconConfig, label }: { iconConfig: PaymentMethodIconConfig; label?: string }) => (
  <div
    title={label}
    className="flex flex-col gap-0.5 items-center justify-center px-2.5 py-2 rounded-[10px] shadow-[0_0_10px_rgba(0,0,0,0.07)] dark:bg-gray-6 min-w-[70px] max-mob:min-w-[50px] max-mob:p-2"
  >
    <PaymentIcon {...iconConfig} className="h-10 max-mob:h-7 w-max" />
  </div>
);

const PaymentMethodsDisplay = ({ paymentMethods, className = "" }: IPaymentMethodsDisplayProps) => {
  const { t } = useTranslation();
  const paymentMethodConfig = useMemo(() => getPaymentMethodConfig(t), [t]);
  const supportedMethods = useMemo(() => {
    return paymentMethods?.filter((method): method is PaymentMethod => (method as PaymentMethod) in paymentMethodConfig);
  }, [JSON.stringify(paymentMethods), paymentMethodConfig]);

  let hasCardPayment = false;
  const nonCardMethods: PaymentMethod[] = [];

  supportedMethods?.forEach((method) => {
    if (method === PaymentMethod.CARD) {
      hasCardPayment = true;
    } else {
      nonCardMethods.push(method);
    }
  });

  if (!paymentMethods?.length) {
    return null;
  }

  const popularCards: [string, ResolvedPaymentConfig][] = [];
  const otherCards: [string, ResolvedPaymentConfig][] = [];

  if (hasCardPayment) {
    Object.entries(CARD_BRAND_CONFIG).forEach(([brand, cfg]) => {
      if (cfg.isMostPopular) {
        popularCards.push([brand, cfg]);
      } else {
        otherCards.push([brand, cfg]);
      }
    });
  }

  const popularMethods: PaymentMethod[] = [];
  const otherMethods: PaymentMethod[] = [];

  nonCardMethods?.forEach((method) => {
    const cfg = paymentMethodConfig[method];
    if (cfg?.isMostPopular) {
      popularMethods.push(method);
    } else {
      otherMethods.push(method);
    }
  });

  return (
    <Col className="gap-lg">
      {(popularCards.length > 0 || popularMethods.length > 0) && (
        <>
          <Text variant={TextVariants.MOBILE_HEADING_6_REGULAR} className="text-center max-mob:text-label-md">
            <Trans i18nKey="subscription:page.paymentMethodsDisplay.mostPopular" />
          </Text>
          <div className={cn("flex justify-center flex-wrap gap-2", className)}>
            {popularCards.map(([brand, cfg]) => (
              <PaymentItem key={brand} iconConfig={cfg.icon} label={cfg.label} />
            ))}

            {popularMethods.map((method) => {
              const cfg = paymentMethodConfig[method];
              if (!cfg) return null;

              return <PaymentItem key={method} iconConfig={cfg.icon} label={cfg.label} />;
            })}
          </div>
        </>
      )}

      {(otherCards.length > 0 || otherMethods.length > 0) && (
        <>
          <Text variant={TextVariants.MOBILE_HEADING_6_REGULAR} className="text-center max-mob:text-label-md">
            <Trans i18nKey="subscription:page.paymentMethodsDisplay.moreOptions" />
          </Text>
          <div className={`flex flex-wrap items-center justify-center gap-2 ${className}`}>
            {otherCards.map(([brand, cfg]) => (
              <PaymentItem key={brand} iconConfig={cfg.icon} label={cfg.label} />
            ))}

            {otherMethods.map((method) => {
              const cfg = paymentMethodConfig[method];
              if (!cfg) return null;

              return <PaymentItem key={method} iconConfig={cfg.icon} label={cfg.label} />;
            })}
          </div>
        </>
      )}
    </Col>
  );
};

export default PaymentMethodsDisplay;
